/**
 * Lab stage is sent on the turn so the backend can rank chips.
 * After POST /coach/turn the Lab renders suggested_chips as returned.
 */

import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
} from "../types/strategyRd";
import type {
  StrategyRdCoachChip,
  StrategyRdCoachLabAction,
  StrategyRdCoachUiMessage,
} from "../types/strategyRdCoach";

export type StrategyRdCoachLabStage =
  | "needs_setup"
  | "ready_to_preview"
  | "previewed_no_confluences"
  | "previewed_with_confluences"
  | "scanned";

export const STRATEGY_RD_COACH_LAB_ACTION_IDS = {
  runPreview: "lab_run_preview",
  addConfluences: "lab_add_confluences",
  configureEntry: "lab_configure_entry",
  explainEntry: "lab_explain_entry",
  explainSides: "lab_explain_sides",
  askWhyNoHits: "lab_ask_why_no_hits",
  tuneSetupWindow: "lab_tune_setup_window",
  openRecipe: "lab_open_recipe",
  openBacktestSettings: "lab_open_backtest_settings",
  /** Offered when a backtest TES left running finishes — TES reviews it only when clicked. */
  reviewBacktest: "lab_review_backtest",
  /** Offered on a backtest opened from history (Backtest history, Versions, links). */
  reviewOpenedBacktest: "lab_review_opened_backtest",
  compareVersions: "lab_compare_versions",
  tryVariation: "lab_try_variation",
  saveStrategy: "lab_save_strategy",
} as const;

export function strategyRdCoachChipForDisplay(
  chip: StrategyRdCoachChip
): StrategyRdCoachChip {
  if (
    chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.askWhyNoHits ||
    chip.id === "why_no_hits"
  ) {
    return chip.label === "Why no matches?" ? chip : { ...chip, label: "Why no matches?" };
  }
  return chip;
}

export function deriveStrategyRdCoachLabStage(args: {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  previewCommitted: boolean;
  hasScanned: boolean;
}): StrategyRdCoachLabStage {
  if (args.hasScanned) return "scanned";
  const setupId = String(args.setup?.id || "").trim();
  if (!setupId) return "needs_setup";
  if (!args.previewCommitted) return "ready_to_preview";
  const confCount = (args.confluences || []).filter((c) =>
    Boolean(String(c?.id || "").trim())
  ).length;
  if (confCount === 0) return "previewed_no_confluences";
  return "previewed_with_confluences";
}

/** Compare FAQ labels: ignore case and a trailing question mark. */
export function strategyRdCoachFaqLabelKey(label: string): string {
  return label.trim().toLowerCase().replace(/\?+$/g, "").trim();
}

/**
 * Drop follow-up chips whose label is already a user message in this chat,
 * including the question just opened.
 */
export function strategyRdCoachChipsExceptAsked(
  chips: StrategyRdCoachChip[] | null | undefined,
  userMessages: string[]
): StrategyRdCoachChip[] {
  const asked = new Set(
    userMessages.map((text) => strategyRdCoachFaqLabelKey(text)).filter(Boolean)
  );
  return (chips || []).filter(
    (chip) => !asked.has(strategyRdCoachFaqLabelKey(chip.label || ""))
  );
}

/** Chips the Lab can act on after a turn. Order is the backend's. */
export function strategyRdCoachChipsForTurn(
  chips: StrategyRdCoachChip[] | null | undefined
): StrategyRdCoachChip[] {
  const seen = new Set<string>();
  const out: StrategyRdCoachChip[] = [];
  for (const chip of chips || []) {
    if (!chip?.id || seen.has(chip.id)) continue;
    // The chart previews on its own: no "Run a Preview" suggestion.
    if (chip.id === STRATEGY_RD_COACH_LAB_ACTION_IDS.runPreview) continue;
    const kept =
      isStrategyRdCoachLabActionChip(chip) ||
      Boolean(chip.style_id || chip.pack_id || chip.faq_id);
    if (!kept) continue;
    seen.add(chip.id);
    out.push(chip);
  }
  return out;
}

export function isStrategyRdCoachLabActionChip(chip: StrategyRdCoachChip): boolean {
  return Object.values(STRATEGY_RD_COACH_LAB_ACTION_IDS).includes(
    chip.id as (typeof STRATEGY_RD_COACH_LAB_ACTION_IDS)[keyof typeof STRATEGY_RD_COACH_LAB_ACTION_IDS]
  );
}

/** A backtest opened from history (Backtest history, Versions, links) while TES shows its chat. */
export type StrategyRdCoachOpenedBacktest = {
  hypothesisId: string;
  name: string;
  version: number | null;
  /** Already saved as a strategy: no "Save as strategy" action. */
  saved: boolean;
  /** The reply whose result card is this backtest (null: not in this chat). */
  messageId: string | null;
  /** Bumped on every open, so reopening the same backtest scrolls to it again. */
  token: number;
};

/**
 * The reply that ran this backtest: its result card first, else the first turn that started a
 * run on it (a variation of it names it as its base too, so the card wins).
 */
export function strategyRdCoachMessageForBacktest(
  messages: StrategyRdCoachUiMessage[],
  hypothesisId: string
): string | null {
  const ran = (pick: (a: StrategyRdCoachLabAction) => unknown) =>
    messages.find((m) => m.role === "assistant" && m.lab_actions?.some((a) => pick(a) === hypothesisId));
  return (ran((a) => a.card?.hypothesis_id) ?? ran((a) => a.hypothesis_id))?.id ?? null;
}

/** "V5 of NY ORB 2-Close", or just the name before versions exist. */
export function strategyRdCoachOpenedBacktestLabel(b: Pick<StrategyRdCoachOpenedBacktest, "name" | "version">): string {
  return b.version && b.version > 1 ? `V${b.version} of ${b.name}` : b.name;
}

/** What to do next with a backtest opened from history. */
export function strategyRdCoachOpenedBacktestChips(b: StrategyRdCoachOpenedBacktest): StrategyRdCoachChip[] {
  const ids = STRATEGY_RD_COACH_LAB_ACTION_IDS;
  return [
    { id: ids.reviewOpenedBacktest, label: "Review this run" },
    ...(b.version && b.version > 1 ? [{ id: ids.compareVersions, label: `Compare to V${b.version - 1}` }] : []),
    { id: ids.tryVariation, label: "Try a variation" },
    ...(b.saved ? [] : [{ id: ids.saveStrategy, label: "Save as strategy" }]),
  ];
}
